import { TicketStatus } from '@cobliteam/common-types';
import { useCompletionBeaconData } from '@cobliteam/hooks/src/useEntity/useCompletionBeaconData';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { usePermissions } from '@cobliteam/permissions';
import { type SchedulingAgentEntryPoint, useSchedulingAgent } from './useSchedulingAgent';

/**
 * "Can we offer installation scheduling, and how do we start it?" — one owner for both surfaces
 * that ask: the nudge in the floating stack and the option in the floating action button.
 *
 * Reads the same queries as the home `UpcomingTicketsCard` (`useCompletionBeaconData`). They are
 * already fetched from the app shell by the nudge and share a react-query key, so consuming this
 * from the FAB adds no requests.
 */
export const usePendingInstallScheduling = () => {
  const { isAccessAllowed } = usePermissions();
  const { ordersAiPage } = useFeatureFlags();
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id ?? '';
  const agent = useSchedulingAgent();

  const hasCrmPermission = isAccessAllowed('create-update-delete:crm');

  /*
   * Gated on the permission, which is free, BEFORE the queries — this hook is mounted in the app
   * shell (floating stack and FAB), so an ungated call would fetch on every page for every fleet.
   * `refetchOnWindowFocus: false` for the same reason: a passive suggestion should not drive a
   * refetch every time the user comes back to the tab. The home beacon keeps the client default.
   */
  const { completionData, upcomingTickets } = useCompletionBeaconData({ fleetId, enabled: hasCrmPermission, refetchOnWindowFocus: false });

  const pendingCount = completionData?.openTicketsToInstall ?? 0;
  const pendingTickets = (upcomingTickets?.tickets ?? []).filter((ticket) => ticket?.type === 'SCHEDULING' && ticket.status === TicketStatus.SCHEDULE_INSTALLATION);

  /*
   * `pendingCount` is an aggregate (`openTicketsToInstall`) and `pendingTickets` comes from the
   * upcoming list — different queries. Scoping the agent to one ticket while the copy announces
   * three would open a conversation about the wrong thing, so both sides have to agree on "one".
   */
  const singleTicketId = pendingCount === 1 && pendingTickets.length === 1 ? pendingTickets[0]?.id : undefined;

  return {
    /*
     * Two different questions, deliberately separate — they used to share one `isAvailable` flag,
     * which forced the FAB entry point and the floating nudge to appear together.
     */
    /** May this user reach the scheduling agent at all? Entry points (the FAB option) use this. */
    canOpenAgent: hasCrmPermission,
    /** Is there something concrete to schedule? The proactive nudge uses this — announcing a count
     *  of zero pending installations would be a card with nothing to say. */
    hasPendingInstalls: hasCrmPermission && pendingCount > 0,
    pendingCount,
    /** Whether the agent will open scoped to one ticket. */
    isScoped: !!singleTicketId,
    /*
     * Com a flag `orders-ai-page`, o FAB leva à apresentação do agente em vez de já mandar um
     * prompt: a página mostra tudo o que ele faz, e escolher o assunto pelo usuário desperdiça
     * justamente a tela que o experimento quer testar.
     *
     * Sem a flag nada muda — o painel flutuante não tem tela de apresentação, então abrir sem
     * mensagem deixaria a pessoa diante de um chat vazio.
     *
     * One pending ticket: the agent opens already scoped to it. Otherwise it starts from the fleet.
     */
    open: (entryPoint: SchedulingAgentEntryPoint) => {
      if (ordersAiPage) return agent.openHome(entryPoint);
      return singleTicketId ? agent.openForTicket(singleTicketId, entryPoint) : agent.openForPendingInstalls(entryPoint);
    },
  };
};

export default usePendingInstallScheduling;
